import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, query, where, onSnapshot,
  getDocs, Timestamp, getAggregateFromServer, sum, increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useListasConfig } from './useListasConfig';
import { useParchear, useParchearHoras } from './DatosContext';
import DialogoConfirmar from './Dialogo';
import useTecladoLista, { ESTILO_ACTIVO } from './useTecladoLista';
import { leerDiasRevisados } from './diasSinTrabajo';

const C = {
  surface:     '#1a1440',
  surface2:    '#221952',
  border:      'rgba(83,74,183,0.25)',
  borderHover: 'rgba(83,74,183,0.5)',
  indigo:      '#534AB7',
  violet:      '#7B6FE8',
  positive:    '#1D9E75',
  negative:    '#D85A30',
  attention:   '#BA7517',
  text:        '#ffffff',
  textMuted:   'rgba(255,255,255,0.5)',
  textSoft:    'rgba(255,255,255,0.75)',
};

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function fromDateStr(s) {
  const [y,m,d] = s.split('-').map(Number); return new Date(y,m-1,d);
}
function displayDate(s) {
  if (!s) return ''; const [y,m,d] = s.split('-'); return `${d}/${m}/${y}`;
}
function getLunes(date) {
  const d = new Date(date), day = d.getDay();
  d.setDate(d.getDate()-(day===0?6:day-1)); return d;
}
function weekDays(lunes) {
  return Array.from({length:7},(_,i)=>{ const d=new Date(lunes); d.setDate(d.getDate()+i); return d; });
}
const DIAS  = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
const DIAS_LARGO = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
const nombreDia = (s) => DIAS_LARGO[fromDateStr(s).getDay()];
const MESES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

function semanaLabel(lunes) {
  const dom = new Date(lunes); dom.setDate(dom.getDate()+6);
  if (lunes.getMonth()===dom.getMonth())
    return `${lunes.getDate()}–${dom.getDate()} de ${MESES[lunes.getMonth()]} ${lunes.getFullYear()}`;
  return `${lunes.getDate()} ${MESES[lunes.getMonth()]} – ${dom.getDate()} ${MESES[dom.getMonth()]} ${dom.getFullYear()}`;
}

// TODO-CONFIG: reemplazar JORNADA_HORAS por empleado.horas_jornada cuando
// exista Configuración. Busca "JORNADA_HORAS" (2 apariciones).
const JORNADA_HORAS = 8;

const fmtH = (n) => {
  const num = parseFloat(n) || 0;
  return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num) + ' h';
};

function colorH(h) {
  if (h<=0) return C.textMuted;
  if (h<JORNADA_HORAS) return C.attention;
  return C.positive;
}
function bgH(h) {
  if (h<=0) return 'rgba(255,255,255,0.04)';
  if (h<JORNADA_HORAS) return 'rgba(186,117,23,0.12)';
  return 'rgba(29,158,117,0.12)';
}
function borderH(h) {
  if (h<=0) return 'rgba(255,255,255,0.08)';
  if (h<JORNADA_HORAS) return 'rgba(186,117,23,0.4)';
  return 'rgba(29,158,117,0.4)';
}

// Búsquedas: norm() en ambos lados (regla de METRON). El código de proyecto puede ser número: siempre String().
const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const FORM_VACIO = {
  esInterno:false, proyectoId:'', proyectoNombre:'', proyectoCodigo:'', horas:'', nota:'', categoria:'',
};

export default function Horas({ usuario, perfil, esAdmin, accionInicial, onAccionCompletada }) {
  // Sesión 33: el rol viene del perfil (Layout). Antes se leía usuario.rol, que no existe en la cuenta de
  // Firebase, y Control de Horas nunca reconocía al administrador.
  const isAdmin = !!esAdmin;
  const today   = toDateStr(new Date());
  const primerDiaMes = today.slice(0,7)+'-01';

  const { categoriasHoras } = useListasConfig(['categorias_horas']);

  const [vista,            setVista]            = useState('dia');
  const [listaEmpleados,   setListaEmpleados]   = useState([]);
  const [empleadoOverride, setEmpleadoOverride] = useState(null);
  const [lunes,            setLunes]            = useState(()=>getLunes(new Date()));
  const [fechaDia,         setFechaDia]         = useState(today);
  const [registrosDia,     setRegistrosDia]     = useState([]);
  const [totalesSemana,    setTotalesSemana]    = useState({});
  const [proyectos,        setProyectos]        = useState([]);

  // Formulario
  const [form,        setForm]        = useState(FORM_VACIO);
  const [editandoId,  setEditandoId]  = useState(null);
  const [proyectoAnterior, setProyectoAnterior] = useState(null); // proyecto del registro antes de editarlo (sesión 33)
  const [horasAnteriores,  setHorasAnteriores]  = useState(0);    // horas del registro antes de editarlo (sesión 33)
  const [mostrarForm, setMostrarForm] = useState(false);
  // Sesión 34: al editar se puede cambiar el día del apunte
  const [fechaEdicion, setFechaEdicion] = useState('');
  // Sesión 34: horas del último día laborable, para avisar de apuntes con la fecha de hoy que eran de ese día
  const [ultimoLaborable, setUltimoLaborable] = useState(null); // { fecha, total }
  const [errorForm,   setErrorForm]   = useState('');
  const [guardando,   setGuardando]   = useState(false);
  const [busqForm,    setBusqForm]    = useState('');
  const [isMobile,    setIsMobile]    = useState(window.innerWidth <= 768);
  // Modal confirmar eliminación
  const [modalEliminarId, setModalEliminarId] = useState(null);
  // Sesión 33: memoria compartida (horas_total en la lista de proyectos y horas de Análisis)
  const parchearProyectos = useParchear('proyectos');
  const parchearHoras = useParchearHoras();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [sugsForm,    setSugsForm]    = useState([]);
  const busqFormRef = useRef(null);

  // Sesión 34: llegar desde el Dashboard ("+ Registrar horas de hoy" o "Apuntar" en un día pendiente) abre el
  // formulario directamente en ese día. Nunca en un día futuro.
  useEffect(() => {
    if (!accionInicial || accionInicial.accion !== 'nuevo') return;
    const f = accionInicial.fecha && accionInicial.fecha <= today ? accionInicial.fecha : today;
    setVista('dia'); setFechaDia(f); setLunes(getLunes(fromDateStr(f)));
    setMostrarForm(true); setEditandoId(null); setForm(FORM_VACIO); setBusqForm(''); setErrorForm('');
    if (onAccionCompletada) onAccionCompletada();
  }, [accionInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  // Histórico
  const [filtro, setFiltro] = useState({
    desde: primerDiaMes,
    hasta: today,
    tipo:  'todos',
    empleadoId: '',
  });
  const [proySeleccionados,   setProySeleccionados]   = useState([]); // [{id,codigo,nombre}]
  const [busqHist,            setBusqHist]            = useState('');
  const [sugsHist,            setSugsHist]            = useState([]);
  const [registrosHistorico,  setRegistrosHistorico]  = useState([]);
  const [cargandoHistorico,   setCargandoHistorico]   = useState(false);
  const busqHistRef = useRef(null);

  const empActivo = isAdmin && empleadoOverride
    ? empleadoOverride
    : { uid: usuario?.uid, nombre: perfil?.nombre || usuario?.email || '' };

  // ── Empleados ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    // Sesión 33: las fichas usan estado:'activo' (la tuya, activo:true). Los históricos no tienen cuenta.
    getDocs(collection(db,'usuarios'))
      .then(snap => setListaEmpleados(snap.docs.map(d=>({uid:d.id,...d.data()}))
        .filter(u => u.email && (u.estado === 'activo' || (u.activo === true && u.estado !== 'historico')))
        .sort((a,b) => (a.nombre||'').localeCompare(b.nombre||'','es'))));
  }, [isAdmin]);

  // ── Proyectos activos ─────────────────────────────────────────────────────
  useEffect(() => {
    // Sesión 33: lista reducida (código, nombre, estado): los empleados no pueden leer la ficha con importes.
    const q = query(collection(db,'proyectos_lista'), where('estado','in',['en_curso','entregado']));
    return onSnapshot(q, snap => setProyectos(
      snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b) => String(a.codigo).localeCompare(String(b.codigo), 'es', { numeric: true }))
    ));
  }, []);

  // ── Listener día ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!empActivo?.uid) return;
    setRegistrosDia([]);
    const q = query(
      collection(db,'registros_horas'),
      where('empleadoId','==',empActivo.uid),
      where('fecha','==',fechaDia)
    );
    const unsub = onSnapshot(q, snap => {
      const regs = snap.docs.map(d=>({id:d.id,...d.data()}));
      regs.sort((a,b)=>(a.creado_en?.toMillis?.()??0)-(b.creado_en?.toMillis?.()??0));
      setRegistrosDia(regs);
    });
    return unsub;
  }, [empActivo?.uid, fechaDia]);

  // ── Listener semana ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!empActivo?.uid) return;
    const fechas = weekDays(lunes).map(d=>toDateStr(d));
    const q = query(
      collection(db,'registros_horas'),
      where('empleadoId','==',empActivo.uid),
      where('fecha','in',fechas)
    );
    const unsub = onSnapshot(q, snap => {
      const tots = {}; fechas.forEach(f=>tots[f]=0);
      snap.docs.forEach(d=>{ const r=d.data(); tots[r.fecha]=(tots[r.fecha]||0)+(parseFloat(r.horas)||0); });
      setTotalesSemana(tots);
    });
    return unsub;
  }, [empActivo?.uid, lunes]);

  // ── Aviso del último día laborable (sesión 34) ────────────────────────────
  // Al apuntar en HOY, se miran las horas del último día laborable (el viernes si hoy es lunes). Si tiene menos
  // de una jornada, se avisa: es el error de apuntar el lunes lo del viernes con la fecha de hoy. 1–4 lecturas,
  // solo de los apuntes propios (las reglas exigen el filtro por empleadoId).
  useEffect(() => {
    setUltimoLaborable(null);
    if (!mostrarForm || editandoId || fechaDia !== today || !empActivo?.uid) return;
    const d = fromDateStr(today);
    do { d.setDate(d.getDate() - 1); } while (d.getDay() === 0 || d.getDay() === 6);
    const fecha = toDateStr(d);
    if (leerDiasRevisados(empActivo.uid).includes(fecha)) return; // día dado por bueno en el Dashboard (sesión 34)
    let vivo = true;
    getDocs(query(collection(db,'registros_horas'), where('empleadoId','==',empActivo.uid), where('fecha','==',fecha)))
      .then(snap => {
        if (!vivo) return;
        const total = snap.docs.reduce((a, x) => a + (parseFloat(x.data().horas) || 0), 0);
        setUltimoLaborable({ fecha, total });
      })
      .catch(err => console.error('No se pudo comprobar el último día laborable', err));
    return () => { vivo = false; };
  }, [mostrarForm, editandoId, fechaDia, today, empActivo?.uid]);

  // ── Sugerencias formulario ────────────────────────────────────────────────
  useEffect(() => {
    if (!busqForm.trim()) { setSugsForm([]); return; }
    const lo = norm(busqForm);
    setSugsForm(proyectos.filter(p=>norm(p.codigo).includes(lo)||norm(p.nombre).includes(lo)).slice(0,6));
  }, [busqForm, proyectos]);

  // ── Sugerencias histórico ─────────────────────────────────────────────────
  useEffect(() => {
    if (!busqHist.trim()) { setSugsHist([]); return; }
    const lo = norm(busqHist);
    setSugsHist(
      proyectos
        .filter(p =>
          (norm(p.codigo).includes(lo) || norm(p.nombre).includes(lo)) &&
          !proySeleccionados.find(s=>s.id===p.id)
        )
        .slice(0,6)
    );
  }, [busqHist, proyectos, proySeleccionados]);

  // Cerrar dropdowns al clic fuera
  useEffect(() => {
    const h = e => {
      if (busqFormRef.current && !busqFormRef.current.contains(e.target)) setSugsForm([]);
      if (busqHistRef.current && !busqHistRef.current.contains(e.target)) setSugsHist([]);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Histórico: carga base cuando cambia rango o empleado ──────────────────
  const cargarHistoricoBase = useCallback(async (desde, hasta, empId) => {
    if (!desde || !hasta) return;
    setCargandoHistorico(true);
    setRegistrosHistorico([]);
    try {
      const uidFiltro = !isAdmin ? usuario?.uid : (empId || null);
      let constraints = [where('fecha','>=',desde), where('fecha','<=',hasta)];
      if (uidFiltro) constraints = [where('empleadoId','==',uidFiltro), ...constraints];
      const snap = await getDocs(query(collection(db,'registros_horas'), ...constraints));
      const regs = snap.docs.map(d=>({id:d.id,...d.data()}));
      regs.sort((a,b)=>{
        if (b.fecha!==a.fecha) return b.fecha.localeCompare(a.fecha);
        return (b.creado_en?.toMillis?.()??0)-(a.creado_en?.toMillis?.()??0);
      });
      setRegistrosHistorico(regs);
    } catch(err) { console.error('Error histórico:',err); }
    finally { setCargandoHistorico(false); }
  }, [isAdmin, usuario?.uid]);

  useEffect(() => {
    if (vista==='historico') cargarHistoricoBase(filtro.desde, filtro.hasta, filtro.empleadoId);
  }, [vista, filtro.desde, filtro.hasta, filtro.empleadoId, cargarHistoricoBase]);

  // ── Filtrado en memoria (tiempo real) ─────────────────────────────────────
  const historicoFiltrado = registrosHistorico.filter(r => {
    if (filtro.tipo==='proyectos' && r.esInterno)  return false;
    if (filtro.tipo==='interno'   && !r.esInterno) return false;
    if (proySeleccionados.length && !proySeleccionados.find(s=>s.id===r.proyectoId)) return false;
    return true;
  });

  // ── horas_total del proyecto (sesión 33) ──────────────────────────────────
  // Administrador: suma en el servidor de TODOS los registros del proyecto (1 lectura), guardada como valor
  // absoluto; corrige cualquier desviación anterior. Resto de usuarios: no pueden leer horas ajenas, así que
  // suman o restan la diferencia de su apunte (las reglas solo les dejan tocar el campo horas_total).
  const ajustarHorasProyectos = async (cambios) => {
    // cambios: [{ pid, delta }]
    if (isAdmin) return recalcularHorasProyectos(cambios.map(c => c.pid));
    const porProyecto = {};
    cambios.forEach(({ pid, delta }) => { if (pid && delta) porProyecto[pid] = (porProyecto[pid] || 0) + delta; });
    for (const [pid, delta] of Object.entries(porProyecto)) {
      if (Math.abs(delta) < 1e-9) continue;
      try { await updateDoc(doc(db,'proyectos',pid), { horas_total: increment(Math.round(delta * 10000) / 10000) }); }
      catch (err) { console.error('No se pudo actualizar horas_total del proyecto', pid, err); }
    }
  };

  const recalcularHorasProyectos = async (ids) => {
    for (const pid of [...new Set(ids.filter(Boolean))]) {
      try {
        const agg = await getAggregateFromServer(
          query(collection(db,'registros_horas'), where('proyectoId','==',pid)),
          { total: sum('horas') }
        );
        const total = Math.round((agg.data().total || 0) * 10000) / 10000;
        await updateDoc(doc(db,'proyectos',pid), { horas_total: total });
        parchearProyectos(items => items.map(p => p.id === pid ? { ...p, horas_total: total } : p));
      } catch (err) { console.error('No se pudo recalcular horas_total del proyecto', pid, err); }
    }
  };

  // ── Guardar / editar ──────────────────────────────────────────────────────
  const guardar = async () => {
    if (!form.esInterno && !form.proyectoId) { setErrorForm('Selecciona un proyecto o marca como Interno/Admin.'); return; }
    const horas = parseFloat(form.horas);
    if (!form.horas || isNaN(horas) || horas<=0) { setErrorForm('Introduce un número de horas válido (ej. 3 o 2.5).'); return; }
    // Sesión 34: fecha razonable al editar (un error al teclear dejaba años como 0006)
    const fechaGuardar = editandoId ? (fechaEdicion || fechaDia) : fechaDia;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaGuardar) || fechaGuardar < '2018-01-01' || fechaGuardar > today) { setErrorForm('Revisa el día del apunte: tiene que ser una fecha entre 2018 y hoy.'); return; }
    setErrorForm(''); setGuardando(true);
    try {
      const datos = {
        empleadoId: empActivo.uid, empleadoNombre: empActivo.nombre,
        fecha: editandoId ? (fechaEdicion || fechaDia) : fechaDia, horas, nota: form.nota.trim(), esInterno: form.esInterno,
        proyectoId:     form.esInterno ? null : form.proyectoId,
        proyectoNombre: form.esInterno ? 'Interno / Admin' : form.proyectoNombre,
        proyectoCodigo: form.esInterno ? null : form.proyectoCodigo,
        categoria:      form.esInterno ? (form.categoria || '') : null,
      };
      let idRegistro = editandoId;
      if (editandoId) { await updateDoc(doc(db,'registros_horas',editandoId), datos); }
      else {
        const ref = await addDoc(collection(db,'registros_horas'),{...datos, creado_en:Timestamp.now()});
        idRegistro = ref.id;
      }
      parchearHoras(idRegistro, datos);
      // Proyecto actual y, si se cambió el proyecto al editar, también el anterior (en segundo plano)
      ajustarHorasProyectos([
        { pid: datos.proyectoId, delta: horas },
        ...(editandoId ? [{ pid: proyectoAnterior, delta: -horasAnteriores }] : []),
      ]);
      cerrarForm();
    } catch(err) { console.error('Error guardando:',err); setErrorForm('Error al guardar. Inténtalo de nuevo.'); }
    finally { setGuardando(false); }
  };

  const cerrarForm = () => {
    setForm(FORM_VACIO); setBusqForm(''); setSugsForm([]);
    setMostrarForm(false); setEditandoId(null); setProyectoAnterior(null); setHorasAnteriores(0); setErrorForm(''); setFechaEdicion('');
  };

  const abrirEdicion = (reg) => {
    setForm({
      esInterno: reg.esInterno||false, proyectoId: reg.proyectoId||'',
      proyectoNombre: reg.proyectoNombre||'', proyectoCodigo: reg.proyectoCodigo||'',
      horas: String(reg.horas), nota: reg.nota||'', categoria: reg.categoria||'',
    });
    setBusqForm(reg.esInterno ? '' : `${reg.proyectoCodigo?reg.proyectoCodigo+' · ':''}${reg.proyectoNombre||''}`);
    setSugsForm([]); setEditandoId(reg.id); setFechaEdicion(reg.fecha || fechaDia); setProyectoAnterior(reg.esInterno ? null : (reg.proyectoId || null));
    setHorasAnteriores(reg.esInterno ? 0 : (parseFloat(reg.horas) || 0));
    setMostrarForm(true); setErrorForm('');
  };

  const eliminar = async (id) => {
    const reg = registrosDia.find(r => r.id === id) || registrosHistorico.find(r => r.id === id);
    await deleteDoc(doc(db,'registros_horas',id));
    setModalEliminarId(null);
    parchearHoras(id, null);
    if (reg && !reg.esInterno && reg.proyectoId) ajustarHorasProyectos([{ pid: reg.proyectoId, delta: -(parseFloat(reg.horas) || 0) }]);
  };

  // ── Selección múltiple proyectos histórico ────────────────────────────────
  // Sesión 34: flechas, Enter y Esc en los buscadores de proyecto
  const elegirProyectoForm = (p) => { setForm(f=>({...f,proyectoId:p.id,proyectoNombre:p.nombre,proyectoCodigo:p.codigo||''})); setBusqForm(`${p.codigo?p.codigo+' · ':''}${p.nombre}`); setSugsForm([]); };
  const teclaForm = useTecladoLista(sugsForm, elegirProyectoForm, () => setSugsForm([]));
  const teclaHist = useTecladoLista(sugsHist, (p) => seleccionarProy(p), () => setSugsHist([]));

  const seleccionarProy = (p) => {
    if (proySeleccionados.find(s=>s.id===p.id)) return;
    setProySeleccionados(prev=>[...prev, {id:p.id, codigo:p.codigo, nombre:p.nombre}]);
    setBusqHist(''); setSugsHist([]);
  };
  const quitarProy = (id) => setProySeleccionados(prev=>prev.filter(s=>s.id!==id));

  const irDia = (f) => { setFechaDia(f); cerrarForm(); };
  const verDiaRegistro = (reg) => {
    irDia(reg.fecha); setLunes(getLunes(fromDateStr(reg.fecha)));
    if(isAdmin&&reg.empleadoId!==usuario?.uid){
      const emp=listaEmpleados.find(x=>x.uid===reg.empleadoId);
      if(emp) setEmpleadoOverride(emp);
    }
    setVista('dia');
  };
  const irHoy = () => { setFechaDia(today); setLunes(getLunes(new Date())); cerrarForm(); };
  const semanaAnterior  = () => { const n=new Date(lunes); n.setDate(n.getDate()-7); setLunes(n); };
  const semanaSiguiente = () => { const n=new Date(lunes); n.setDate(n.getDate()+7); setLunes(n); };

  const totalDia    = registrosDia.reduce((a,r)=>a+(parseFloat(r.horas)||0),0);
  const totalSemana = Object.values(totalesSemana).reduce((a,h)=>a+h,0);
  const totalHist   = historicoFiltrado.reduce((a,r)=>a+(parseFloat(r.horas)||0),0);

  return (
    <div style={{padding:'0', maxWidth:'1100px', margin:'0 auto', fontFamily:"'DM Sans', sans-serif"}}>

      {/* Modal confirmar eliminación */}
      {modalEliminarId && (
        <DialogoConfirmar
          titulo="Eliminar registro"
          mensaje="¿Seguro que quieres eliminar este registro de horas? Esta acción no se puede deshacer."
          textoSeguro="No eliminar"
          textoPeligroso="Sí, eliminar"
          onSeguro={()=>setModalEliminarId(null)}
          onPeligroso={()=>eliminar(modalEliminarId)}
        />
      )}

      <style>{`
        .metron-date::-webkit-calendar-picker-indicator {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.6)' stroke-width='2'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'/%3E%3Cline x1='16' y1='2' x2='16' y2='6'/%3E%3Cline x1='8' y1='2' x2='8' y2='6'/%3E%3Cline x1='3' y1='10' x2='21' y2='10'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-size: 16px; cursor: pointer; opacity: 1;
        }
        .metron-date { color-scheme: dark; }
        .sug-item:hover { background: rgba(83,74,183,0.15) !important; }
        .pill-tipo { transition: all 0.15s; }
      `}</style>

      {/* Cabecera */}
      <div style={{position:'sticky', top:0, zIndex:50, background:'#0e0b24', borderBottom:'1px solid rgba(83,74,183,0.2)', padding:'20px 24px 16px', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px'}}>
        <div>
          <h1 style={{margin:0, fontSize:'22px', fontWeight:600, color:C.text}}>Control de horas</h1>
          <p style={{margin:'4px 0 0', fontSize:'13px', color:C.textMuted}}>Registra tus horas al final de cada día de trabajo</p>
        </div>
        {isAdmin && (
          <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
            <span style={{fontSize:'13px', color:C.textMuted}}>Registrando para:</span>
            <select value={empleadoOverride?.uid||''} onChange={e=>{
              const emp=listaEmpleados.find(x=>x.uid===e.target.value)||null;
              setEmpleadoOverride(emp); cerrarForm();
            }} style={S.select}>
              <option value=''>Yo mismo</option>
              {listaEmpleados.filter(x=>x.uid!==usuario?.uid).map(x=>(
                <option key={x.uid} value={x.uid}>{x.nombre||x.email}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{padding:'20px 24px 24px'}}>
      {/* Tabs */}
      <div style={{display:'flex', gap:'4px', marginBottom:'24px', background:C.surface, borderRadius:'10px', padding:'4px', width:'fit-content'}}>
        {[{id:'dia',label:'Registro diario'},{id:'historico',label:'Histórico'}].map(t=>(
          <button key={t.id} onClick={()=>setVista(t.id)} style={{
            padding:'8px 20px', borderRadius:'7px', border:'none', cursor:'pointer',
            fontSize:'13px', fontWeight:vista===t.id?600:400,
            background:vista===t.id?C.indigo:'transparent',
            color:vista===t.id?'#fff':C.textMuted,
            transition:'all 0.15s', fontFamily:"'DM Sans', sans-serif",
          }}>{t.label}</button>
        ))}
      </div>

      {/* ══ VISTA DIARIA ══════════════════════════════════════════════════════ */}
      {vista==='dia' && (<>

        {/* Semana */}
        <div style={{...S.card, marginBottom:'20px'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px', flexWrap:'wrap', gap:'10px'}}>
            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
              <button onClick={semanaAnterior} style={S.btnIcono}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span style={{fontSize:'13px', fontWeight:500, color:C.textSoft}}>{semanaLabel(lunes)}</span>
              <button onClick={semanaSiguiente} style={S.btnIcono}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
              <div style={{padding:'6px 14px', borderRadius:'8px', background:bgH(totalSemana), border:`1px solid ${borderH(totalSemana)}`, textAlign:'center'}}>
                <div style={{fontSize:'16px', fontWeight:700, whiteSpace:'nowrap', color:totalSemana===0?C.textMuted:colorH(totalSemana)}}>
                  {totalSemana>0?fmtH(totalSemana):'–'}
                </div>
                <div style={{fontSize:'10px', color:C.textMuted}}>Total semana</div>
              </div>
              <button onClick={irHoy} style={{...S.btnSecundario, fontSize:'12px', padding:'5px 12px'}}>Hoy</button>
            </div>
          </div>

          <div className="metron-semana-scroll">
            <div className="metron-semana-grid" style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:'6px'}}>
            {weekDays(lunes).map((dia,i)=>{
              const fStr=toDateStr(dia), horas=totalesSemana[fStr]||0;
              const esHoy=fStr===today, esSel=fStr===fechaDia, esFuturo=fStr>today;
              return (
                <button key={fStr} onClick={()=>{irDia(fStr); setLunes(getLunes(dia));}} disabled={esFuturo}
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center',
                    padding:'10px 6px', borderRadius:'8px', cursor:esFuturo?'default':'pointer',
                    border:esSel?`2px solid ${C.indigo}`:esHoy?`2px solid rgba(83,74,183,0.45)`:'2px solid transparent',
                    background:esSel?'rgba(83,74,183,0.18)':esFuturo?'rgba(255,255,255,0.02)':bgH(horas),
                    opacity:esFuturo?0.35:1, transition:'all 0.15s', fontFamily:"'DM Sans', sans-serif",
                  }}>
                  <span style={{fontSize:'11px', color:C.textMuted, fontWeight:500, marginBottom:'4px'}}>{DIAS[i]}</span>
                  <span style={{fontSize:'14px', fontWeight:600, color:esHoy?C.violet:C.text, marginBottom:'4px'}}>{dia.getDate()}</span>
                  {!esFuturo && (
                    <span style={{fontSize:'11px', fontWeight:700, whiteSpace:'nowrap', color:horas===0?C.textMuted:colorH(horas)}}>
                      {horas===0?'–':fmtH(horas)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          </div>

          <div style={{display:'flex', gap:'16px', marginTop:'12px', paddingTop:'12px', borderTop:`1px solid ${C.border}`, flexWrap:'wrap'}}>
            {[
              {color:C.positive,  bg:'rgba(29,158,117,0.12)',  label:`≥ ${JORNADA_HORAS}h — Completo`},
              {color:C.attention, bg:'rgba(186,117,23,0.12)',  label:`1–${JORNADA_HORAS-1}h — Parcial`},
              {color:C.textMuted, bg:'rgba(255,255,255,0.04)', label:'Sin registrar'},
            ].map(item=>(
              <div key={item.label} style={{display:'flex', alignItems:'center', gap:'6px'}}>
                <div style={{width:'12px', height:'12px', borderRadius:'3px', background:item.bg, border:`1px solid ${item.color}`}}/>
                <span style={{fontSize:'11px', color:C.textMuted}}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Panel día */}
        <div style={S.card}>
          <div style={{marginBottom:'16px'}}>
            {/* Fila 1: input fecha + etiqueta fecha+HOY */}
            <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px'}}>
              <input type="date" className="metron-date" value={fechaDia} max={today}
                onChange={e=>{ if(!e.target.value) return; irDia(e.target.value); setLunes(getLunes(fromDateStr(e.target.value))); }}
                style={S.inputDate}
              />
              <div style={{flex:1, minWidth:0}}>
                <div style={{display:'flex', alignItems:'center', gap:'6px', flexWrap:'nowrap'}}>
                  <span style={{fontSize:'15px', fontWeight:600, color:C.text, whiteSpace:'nowrap'}}>{displayDate(fechaDia)}</span>
                  {fechaDia===today && <span style={{fontSize:'11px', background:C.indigo, color:'#fff', borderRadius:'4px', padding:'2px 7px', whiteSpace:'nowrap'}}>HOY</span>}
                </div>
                <div style={{fontSize:'12px', color:C.textMuted, marginTop:'2px'}}>
                  {isAdmin&&empleadoOverride?`Registrando para ${empleadoOverride.nombre}`:'Mis horas'}
                </div>
              </div>
            </div>
            {/* Fila 2: total día + botón */}
            <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
              <div style={{padding:'8px 16px', borderRadius:'8px', textAlign:'center', background:bgH(totalDia), border:`1px solid ${borderH(totalDia)}`}}>
                <div style={{fontSize:'22px', fontWeight:700, whiteSpace:'nowrap', color:totalDia===0?C.textMuted:colorH(totalDia)}}>
                  {totalDia>0?fmtH(totalDia):'–'}
                </div>
                <div style={{fontSize:'11px', color:C.textMuted}}>Total día</div>
              </div>
              {!mostrarForm && (
                <button onClick={()=>{setMostrarForm(true); setEditandoId(null); setForm(FORM_VACIO); setBusqForm('');}} style={S.btnPrimario}>
                  + Añadir horas
                </button>
              )}
            </div>
          </div>

          {/* Formulario */}
          {mostrarForm && (
            <div style={{background:'rgba(83,74,183,0.08)', border:`1px solid ${C.borderHover}`, borderRadius:'10px', padding:'16px', marginBottom:'16px'}}>
              <div style={{fontSize:'13px', fontWeight:600, color:C.violet, marginBottom:'14px'}}>
                {editandoId?'Editar registro':'Nueva línea de horas'}
              </div>
              <div style={{marginBottom:'14px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                  <button onClick={()=>{ setForm(f=>({...f,esInterno:!f.esInterno,proyectoId:'',proyectoNombre:'',proyectoCodigo:''})); setBusqForm(''); setSugsForm([]); }}
                    style={{display:'flex', alignItems:'center', gap:'8px', padding:'7px 14px', borderRadius:'7px', cursor:'pointer',
                      border:`1px solid ${form.esInterno?C.violet:C.border}`,
                      background:form.esInterno?'rgba(123,111,232,0.15)':'transparent',
                      color:form.esInterno?C.violet:C.textMuted,
                      fontSize:'13px', transition:'all 0.15s', fontFamily:"'DM Sans', sans-serif", whiteSpace:'nowrap'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    </svg>
                    Interno / Admin
                  </button>
                  <span style={{fontSize:'12px', color:C.textMuted}}>{form.esInterno?'Sin proyecto asignado':'Asignar a un proyecto'}</span>
                </div>
              </div>

              {form.esInterno && (
                <div style={{marginBottom:'14px'}}>
                  <label style={S.label}>Categoría</label>
                  <select
                    value={form.categoria}
                    onChange={e=>setForm(f=>({...f,categoria:e.target.value}))}
                    style={{...S.select, width:'100%', maxWidth:'100%'}}
                  >
                    <option value=''>Sin categoría</option>
                    {categoriasHoras.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}

              {!form.esInterno && (
                <div style={{position:'relative', marginBottom:'14px'}} ref={busqFormRef}>
                  <label style={S.label}>Proyecto *</label>
                  <input type="text" placeholder="Buscar por código o nombre..." value={busqForm} autoComplete="off"
                    onChange={e=>{ setBusqForm(e.target.value); if(!e.target.value) setForm(f=>({...f,proyectoId:'',proyectoNombre:'',proyectoCodigo:''})); }}
                    onKeyDown={teclaForm.onKeyDown}
                    style={S.input}
                  />
                  {sugsForm.length>0 && (
                    <div style={{...S.dropdown, position:'static', marginTop:'4px'}}>
                      {sugsForm.map((p,i)=>(
                        <button key={p.id} className="sug-item" onMouseDown={e=>e.preventDefault()}
                          onMouseEnter={()=>teclaForm.setActivo(i)}
                          onClick={()=>elegirProyectoForm(p)}
                          style={{...S.dropdownItem, ...(i===teclaForm.activo?ESTILO_ACTIVO:{})}}>
                          {p.codigo && <span style={{color:C.violet,fontWeight:700,marginRight:'8px',fontSize:'12px'}}>{p.codigo}</span>}
                          <span style={{color:C.text}}>{p.nombre}</span>
                          {p.sector && <span style={{marginLeft:'auto',color:C.textMuted,fontSize:'11px'}}>{p.sector}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {form.proyectoId
                    ? <div style={{fontSize:'12px',color:C.positive,marginTop:'4px'}}>✓ Proyecto seleccionado</div>
                    : busqForm&&!sugsForm.length ? <div style={{fontSize:'12px',color:C.textMuted,marginTop:'4px'}}>Sin resultados</div> : null
                  }
                </div>
              )}

              {editandoId && (
                <div style={{marginBottom:'14px'}}>
                  <label style={S.label}>Día del apunte</label>
                  <input type="date" className="metron-date" value={fechaEdicion} max={today}
                    onChange={e=>{ if(e.target.value) setFechaEdicion(e.target.value); }} style={S.inputDate}/>
                  {fechaEdicion && fechaEdicion!==fechaDia && (
                    <div style={{fontSize:'12px',color:C.attention,marginTop:'4px'}}>Se moverá al {displayDate(fechaEdicion)} al guardar.</div>
                  )}
                </div>
              )}

              <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '140px 1fr', gap:'10px', marginBottom:'14px'}}>
                <div>
                  <label style={S.label}>Horas *</label>
                  <input type="number" min="0.25" max="24" step="0.25" placeholder="ej. 3.5" value={form.horas}
                    onChange={e=>setForm(f=>({...f,horas:e.target.value}))} style={{...S.input, maxWidth:'160px'}}/>
                </div>
                <div>
                  <label style={S.label}>Nota (opcional)</label>
                  <input type="text" placeholder="Descripción del trabajo..." value={form.nota}
                    onChange={e=>setForm(f=>({...f,nota:e.target.value}))} style={S.input}/>
                </div>
              </div>

              {!editandoId && ultimoLaborable && ultimoLaborable.total < JORNADA_HORAS && (
                <div style={{fontSize:'13px',color:C.attention,marginBottom:'12px',padding:'10px 12px',background:'rgba(186,117,23,0.12)',border:'1px solid rgba(186,117,23,0.4)',borderRadius:'8px',display:'flex',gap:'10px',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap'}}>
                  <span>
                    Se apuntarán en hoy, {nombreDia(today)} {displayDate(today).slice(0,5)}. El {nombreDia(ultimoLaborable.fecha)} {displayDate(ultimoLaborable.fecha).slice(0,5)} {ultimoLaborable.total>0 ? `solo tiene ${fmtH(ultimoLaborable.total)} apuntadas` : 'no tiene horas apuntadas'}: si estas horas son de ese día, cámbialo antes de guardar.
                  </span>
                  <button onClick={()=>{ setFechaDia(ultimoLaborable.fecha); setLunes(getLunes(fromDateStr(ultimoLaborable.fecha))); }}
                    style={{...S.btnSecundario, color:C.attention, borderColor:'rgba(186,117,23,0.5)', whiteSpace:'nowrap'}}>
                    Apuntar en {nombreDia(ultimoLaborable.fecha)} {displayDate(ultimoLaborable.fecha).slice(0,5)}
                  </button>
                </div>
              )}

              {errorForm && (
                <div style={{fontSize:'13px',color:C.negative,marginBottom:'12px',padding:'8px 12px',background:'rgba(216,90,48,0.1)',borderRadius:'6px'}}>
                  {errorForm}
                </div>
              )}
              <div style={{display:'flex', gap:'10px'}}>
                <button onClick={guardar} disabled={guardando} style={S.btnPrimario}>
                  {guardando?'Guardando...':editandoId?'Guardar cambios':'Guardar'}
                </button>
                <button onClick={cerrarForm} style={S.btnSecundario}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Lista registros */}
          {registrosDia.length===0 ? (
            <div style={{textAlign:'center', padding:'36px 16px', color:C.textMuted}}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                style={{opacity:0.35, display:'block', margin:'0 auto 12px'}}>
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <div style={{fontSize:'14px'}}>Sin horas registradas este día</div>
              <div style={{fontSize:'12px', marginTop:'4px'}}>Pulsa «Añadir horas» para empezar</div>
            </div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
              {registrosDia.map(reg=>(
                <div key={reg.id} style={{display:'flex', alignItems:'center', gap: isMobile ? '8px' : '12px', flexWrap: isMobile ? 'wrap' : 'nowrap', padding:'12px 14px', borderRadius:'8px', background:C.surface2, border:`1px solid ${C.border}`}}>
                  <div style={{width:'36px',height:'36px',borderRadius:'8px',flexShrink:0,
                    background:reg.esInterno?'rgba(123,111,232,0.15)':'rgba(83,74,183,0.15)',
                    display:isMobile?'none':'flex',alignItems:'center',justifyContent:'center'}}>
                    {reg.esInterno
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.violet} strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.indigo} strokeWidth="2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                    }
                  </div>
                  <div style={{flex:1, minWidth:0, flexBasis: isMobile ? '100%' : 'auto'}}>
                    <div style={{fontSize:'14px',fontWeight:600,color:C.text,display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                      {reg.esInterno
                        ? <><span style={{color:C.violet}}>Interno / Admin</span>{reg.categoria&&<span style={{fontSize:'11px',background:'rgba(123,111,232,0.15)',color:C.violet,borderRadius:'4px',padding:'2px 8px'}}>{reg.categoria}</span>}</>
                        : <>{reg.proyectoCodigo&&<span style={{color:C.violet,fontSize:'12px',fontWeight:700}}>{reg.proyectoCodigo}</span>}<span>{reg.proyectoNombre}</span></>
                      }
                    </div>
                    {reg.nota&&<div style={{fontSize:'12px',color:C.textMuted,marginTop:'2px'}}>{reg.nota}</div>}
                  </div>
                  <div style={{fontSize:isMobile?'16px':'20px',fontWeight:700,color:C.text,flexShrink:0,whiteSpace:'nowrap',marginLeft: isMobile ? 'auto' : 0}}>{fmtH(reg.horas)}</div>
                  <div style={{display:'flex',gap:'6px',flexShrink:0}}>
                    <button onClick={()=>abrirEdicion(reg)} style={S.btnIcono} title="Editar">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button onClick={()=>setModalEliminarId(reg.id)} style={{...S.btnIcono,color:C.negative}} title="Eliminar">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              {registrosDia.length>1 && (
                <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',padding:'10px 14px',borderTop:`1px solid ${C.border}`,marginTop:'4px',gap:'12px'}}>
                  <span style={{fontSize:'13px',color:C.textMuted}}>Total del día</span>
                  <span style={{fontSize:'22px',fontWeight:700,color:colorH(totalDia)}}>{fmtH(totalDia)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </>)}

      {/* ══ HISTÓRICO ═════════════════════════════════════════════════════════ */}
      {vista==='historico' && (
        <div style={S.card}>

          {/* Fila 1: buscador proyecto + desde + hasta */}
          <div style={{display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto auto', gap:'10px', marginBottom:'10px', alignItems:'end'}}>

            {/* Buscador con selección múltiple */}
            <div>
              <label style={S.label}>Proyecto</label>
              {/* Etiquetas seleccionadas */}
              {proySeleccionados.length>0 && (
                <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'6px'}}>
                  {proySeleccionados.map(p=>(
                    <span key={p.id} style={{
                      display:'inline-flex', alignItems:'center', gap:'6px',
                      padding:'4px 10px', borderRadius:'20px',
                      background:'rgba(83,74,183,0.15)',
                      border:'1px solid rgba(83,74,183,0.35)',
                      fontSize:'12px', color:C.violet, fontWeight:500,
                    }}>
                      {p.codigo&&<span style={{fontWeight:700}}>{p.codigo}</span>}
                      {p.nombre}
                      <button onClick={()=>quitarProy(p.id)} style={{
                        background:'none', border:'none', cursor:'pointer',
                        color:'rgba(123,111,232,0.7)', fontSize:'16px', lineHeight:1,
                        padding:0, display:'flex', alignItems:'center',
                      }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              {/* Input búsqueda */}
              <div style={{position:'relative'}} ref={busqHistRef}>
                <input type="text" placeholder="Buscar por código o nombre..." value={busqHist} autoComplete="off"
                  onChange={e=>setBusqHist(e.target.value)}
                  onKeyDown={teclaHist.onKeyDown}
                  style={S.input}
                />
                {(sugsHist.length>0 || (busqHist.length===0 && proySeleccionados.length===0)) && busqHist.length>0 && (
                  <div style={S.dropdown}>
                    {sugsHist.map((p,i)=>(
                      <button key={p.id} className="sug-item" onMouseDown={e=>e.preventDefault()}
                        onMouseEnter={()=>teclaHist.setActivo(i)}
                        onClick={()=>seleccionarProy(p)}
                        style={{...S.dropdownItem, ...(i===teclaHist.activo?ESTILO_ACTIVO:{})}}>
                        {p.codigo&&<span style={{color:C.violet,fontWeight:700,marginRight:'8px',fontSize:'12px'}}>{p.codigo}</span>}
                        <span style={{color:C.text}}>{p.nombre}</span>
                        {p.sector&&<span style={{marginLeft:'auto',color:C.textMuted,fontSize:'11px'}}>{p.sector}</span>}
                      </button>
                    ))}
                    {/* Mensaje de ayuda — solo si no hay ninguno seleccionado */}
                    {proySeleccionados.length===0 && sugsHist.length>0 && (
                      <div style={{padding:'8px 14px', borderTop:`1px solid ${C.border}`, fontSize:'11px', color:C.textMuted, fontStyle:'italic'}}>
                        Puedes seleccionar uno o varios proyectos
                      </div>
                    )}
                    {sugsHist.length===0 && (
                      <div style={{padding:'10px 14px', fontSize:'13px', color:C.textMuted}}>Sin resultados</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Desde */}
            <div>
              <label style={S.label}>Desde</label>
              <input type="date" className="metron-date" value={filtro.desde} max={filtro.hasta||today}
                onChange={e=>e.target.value&&setFiltro(f=>({...f,desde:e.target.value}))}
                style={S.inputDate}/>
            </div>

            {/* Hasta */}
            <div>
              <label style={S.label}>Hasta</label>
              <input type="date" className="metron-date" value={filtro.hasta} min={filtro.desde} max={today}
                onChange={e=>e.target.value&&setFiltro(f=>({...f,hasta:e.target.value}))}
                style={S.inputDate}/>
            </div>
          </div>

          {/* Fila 2: pills tipo + selector empleado */}
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', flexWrap:'wrap', gap:'10px'}}>
            <div style={{display:'flex', gap:'6px'}}>
              {[{id:'todos',label:'Todos'},{id:'proyectos',label:'Proyectos'},{id:'interno',label:'Interno / Admin'}].map(p=>(
                <button key={p.id} className="pill-tipo" onClick={()=>setFiltro(f=>({...f,tipo:p.id}))}
                  style={{
                    padding:'6px 14px', borderRadius:'20px', border:'none', cursor:'pointer',
                    fontSize:'12px', fontWeight:filtro.tipo===p.id?600:400,
                    background:filtro.tipo===p.id?C.indigo:'rgba(255,255,255,0.06)',
                    color:filtro.tipo===p.id?'#fff':C.textMuted,
                    fontFamily:"'DM Sans', sans-serif",
                  }}>{p.label}</button>
              ))}
            </div>
            {isAdmin && (
              <select value={filtro.empleadoId} onChange={e=>setFiltro(f=>({...f,empleadoId:e.target.value}))}
                style={{...S.select, width:'auto', minWidth:'180px'}}>
                <option value="">Todos los empleados</option>
                {listaEmpleados.map(x=>(
                  <option key={x.uid} value={x.uid}>{x.nombre||x.email}</option>
                ))}
              </select>
            )}
          </div>

          {/* Resumen KPIs */}
          {!cargandoHistorico && historicoFiltrado.length>0 && (
            <div style={{display:'flex', gap:'24px', padding:'12px 16px', background:C.surface2, borderRadius:'8px', marginBottom:'16px', flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:'11px',color:C.textMuted,marginBottom:'2px'}}>Registros</div>
                <div style={{fontSize:'20px',fontWeight:700,color:C.text}}>{historicoFiltrado.length}</div>
              </div>
              <div>
                <div style={{fontSize:'11px',color:C.textMuted,marginBottom:'2px'}}>Total horas</div>
                <div style={{fontSize:'20px',fontWeight:700,color:C.violet}}>{fmtH(totalHist)}</div>
              </div>
            </div>
          )}

          {/* Tabla */}
          {cargandoHistorico ? (
            <div style={{textAlign:'center',padding:'32px',color:C.textMuted}}>Cargando...</div>
          ) : historicoFiltrado.length===0 ? (
            <div style={{textAlign:'center',padding:'32px',color:C.textMuted}}>
              No hay registros en el rango seleccionado
            </div>
          ) : isMobile ? (
            // Sesión 34: en el móvil, tarjetas en lugar de tabla (antes las horas y las notas se partían en varias líneas)
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
              {historicoFiltrado.map(reg=>(
                <div key={reg.id} style={{padding:'12px 14px', borderRadius:'8px', background:C.surface2, border:`1px solid ${C.border}`}}>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'8px', marginBottom:'6px'}}>
                    <span style={{fontSize:'12px', color:C.textSoft, fontVariantNumeric:'tabular-nums'}}>{displayDate(reg.fecha)}{isAdmin && reg.empleadoNombre ? ` · ${reg.empleadoNombre}` : ''}</span>
                    <span style={{fontSize:'15px', fontWeight:700, color:C.text, whiteSpace:'nowrap'}}>{fmtH(reg.horas)}</span>
                  </div>
                  <div style={{fontSize:'13px', color:C.text, lineHeight:1.4}}>
                    {reg.esInterno
                      ? <><span style={{color:C.violet}}>Interno / Admin</span>{reg.categoria && <span style={{marginLeft:'6px', fontSize:'11px', background:'rgba(123,111,232,0.15)', color:C.violet, borderRadius:'4px', padding:'2px 7px', whiteSpace:'nowrap'}}>{reg.categoria}</span>}</>
                      : <>{reg.proyectoCodigo && <span style={{color:C.violet, fontWeight:700, marginRight:'6px', fontSize:'12px'}}>{reg.proyectoCodigo}</span>}{reg.proyectoNombre}</>}
                  </div>
                  {reg.nota && <div style={{fontSize:'12px', color:C.textMuted, marginTop:'4px'}}>{reg.nota}</div>}
                  <div style={{display:'flex', justifyContent:'flex-end', marginTop:'8px'}}>
                    <button onClick={()=>verDiaRegistro(reg)} style={{...S.btnIcono, fontSize:'11px', padding:'4px 10px', whiteSpace:'nowrap'}}>Ver día</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr>
                    {['Fecha','Proyecto',...(isAdmin?['Empleado']:[]),'Horas','Nota',''].map(col=>(
                      <th key={col} style={S.th}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historicoFiltrado.map((reg,i)=>(
                    <tr key={reg.id} style={{background:i%2===0?'transparent':'rgba(255,255,255,0.02)'}}>
                      <td style={S.td}><span style={{fontSize:'13px',color:C.textSoft,fontVariantNumeric:'tabular-nums'}}>{displayDate(reg.fecha)}</span></td>
                      <td style={S.td}>
                        {reg.esInterno
                          ? <span style={{fontSize:'13px'}}>
                              <span style={{color:C.violet}}>Interno / Admin</span>
                              {reg.categoria && <span style={{marginLeft:'8px', fontSize:'11px', background:'rgba(123,111,232,0.15)', color:C.violet, borderRadius:'4px', padding:'2px 7px'}}>{reg.categoria}</span>}
                            </span>
                          : <span style={{fontSize:'13px',color:C.text}}>
                              {reg.proyectoCodigo&&<span style={{color:C.violet,fontWeight:700,marginRight:'6px',fontSize:'12px'}}>{reg.proyectoCodigo}</span>}
                              {reg.proyectoNombre}
                            </span>
                        }
                      </td>
                      {isAdmin&&<td style={S.td}><span style={{fontSize:'13px',color:C.textSoft}}>{reg.empleadoNombre}</span></td>}
                      <td style={{...S.td,textAlign:'right'}}><span style={{fontSize:'15px',fontWeight:700,color:C.text}}>{fmtH(reg.horas)}</span></td>
                      <td style={S.td}><span style={{fontSize:'12px',color:C.textMuted}}>{reg.nota||'—'}</span></td>
                      <td style={{...S.td,textAlign:'right'}}>
                        <button onClick={()=>verDiaRegistro(reg)} style={{...S.btnIcono,fontSize:'11px',padding:'4px 10px',whiteSpace:'nowrap'}}>
                          Ver día
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

const S = {
  card:          {background:'#1a1440', border:'1px solid rgba(83,74,183,0.25)', borderRadius:'12px', padding:'20px'},
  input:         {width:'100%', padding:'9px 12px', borderRadius:'8px', border:'1px solid rgba(83,74,183,0.3)', background:'rgba(255,255,255,0.05)', color:'#ffffff', fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'DM Sans', sans-serif"},
  inputDate:     {padding:'9px 12px', borderRadius:'8px', border:'1px solid rgba(83,74,183,0.3)', background:'rgba(255,255,255,0.05)', color:'#ffffff', fontSize:'13px', outline:'none', fontFamily:"'DM Sans', sans-serif", colorScheme:'dark'},
  select:        {width:'100%', padding:'9px 12px', borderRadius:'8px', border:'1px solid rgba(83,74,183,0.3)', background:'#1a1440', color:'#ffffff', fontSize:'13px', outline:'none', boxSizing:'border-box', fontFamily:"'DM Sans', sans-serif", cursor:'pointer'},
  label:         {display:'block', fontSize:'12px', color:'rgba(255,255,255,0.5)', marginBottom:'5px', fontWeight:500},
  btnPrimario:   {padding:'9px 18px', borderRadius:'8px', border:'none', background:'#534AB7', color:'#ffffff', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans', sans-serif"},
  btnSecundario: {padding:'9px 18px', borderRadius:'8px', border:'1px solid rgba(83,74,183,0.3)', background:'transparent', color:'rgba(255,255,255,0.7)', fontSize:'13px', fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans', sans-serif"},
  btnIcono:      {display:'flex', alignItems:'center', justifyContent:'center', padding:'7px', borderRadius:'7px', border:'1px solid rgba(83,74,183,0.25)', background:'transparent', color:'rgba(255,255,255,0.6)', cursor:'pointer'},
  dropdown:      {position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#1a1440', border:'1px solid rgba(83,74,183,0.4)', borderRadius:'8px', zIndex:100, boxShadow:'0 8px 24px rgba(0,0,0,0.5)', overflow:'hidden'},
  dropdownItem:  {display:'flex', alignItems:'center', width:'100%', padding:'10px 14px', border:'none', borderBottom:'1px solid rgba(83,74,183,0.12)', background:'transparent', color:'#ffffff', cursor:'pointer', fontSize:'13px', textAlign:'left', fontFamily:"'DM Sans', sans-serif"},
  th:            {padding:'10px 12px', textAlign:'left', fontSize:'11px', fontWeight:600, color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'1px solid rgba(83,74,183,0.2)'},
  td:            {padding:'11px 12px', borderBottom:'1px solid rgba(83,74,183,0.1)', verticalAlign:'middle'},
};
